import { queryClient, useApiClient } from '@m43/core';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { QueueViewsResource } from '@mark43/cad-api';
import { useQuery } from '@tanstack/react-query';
import { runInAction } from 'mobx';
import { useIntegratedStreams } from '../common/hooks/use-integrated-streams';
import { useEventQueueViews } from './event-queue-views-provider';

export function useIntegratedEventQueueViews() {
    const { eventQueueViewsRef, getListeners } = useEventQueueViews();
    const apiClient = useApiClient();
    const enabledFeatures = useEnabledFeatures();

    const eventQueueViewsBootstrapQuery = useQuery({
        queryKey: ['event-queue-views-bootstrap'],
        queryFn() {
            return apiClient.request<QueueViewsResource.BootstrapActiveEventQueueViews>({
                method: 'POST',
                url: 'queue_views/event_queue_views/bootstrap',
                data: {
                    agencyTypeGlobalAttrIds: [],
                    dispatchAreaIds: [],
                    queueColumnFilters: [],
                    eventQueue: 'DISPATCH',
                    isMultiAgencySubdivisionsEnabled: enabledFeatures.multiAgencySubdivisions,
                },
            });
        },
        onSuccess(data) {
            const eventQueueViews = data.data.data;
            if (eventQueueViews) {
                eventQueueViewsRef.current = Object.fromEntries(
                    eventQueueViews.map((eventQueueView) => [eventQueueView.id, eventQueueView])
                );
                getListeners?.().forEach((listener) => listener.onBootstrap());
            }
        },
    });

    useIntegratedStreams({
        query: eventQueueViewsBootstrapQuery,
        streamNames: ['eventQueues'],
        onMessage(message) {
            message.payload.eventQueueViews.forEach((eventQueueView) => {
                eventQueueViewsRef.current[eventQueueView.id] = eventQueueView;
            });
            message.payload.clearedEventIds.forEach((id) => {
                delete eventQueueViewsRef.current[id];
            });
            runInAction(() => {
                getListeners?.().forEach((listener) => listener.onUpdate(message.payload));
            });
        },
        onReconnect(socketStatusChangeEvent) {
            const messagePosition =
                socketStatusChangeEvent.streamsLastMessageMapBeforeSocketClosure?.['eventQueues']
                    ?.messagePosition;
            if (messagePosition) {
                return apiClient
                    .request<QueueViewsResource.CatchupEventQueueViews>({
                        method: 'POST',
                        url: 'queue_views/event_queue_views/catchup/',
                        data: {
                            offset: messagePosition,
                        },
                    })
                    .then((response) => {
                        const shouldBootstrap = response.data.data?.shouldBootstrap;
                        const bundles = response.data.data?.orderedEventQueuesBundles;
                        if (shouldBootstrap) {
                            eventQueueViewsBootstrapQuery.refetch();
                        } else if (bundles) {
                            bundles.forEach((bundle) => {
                                bundle.eventQueueViews.forEach((eventQueueView) => {
                                    eventQueueViewsRef.current[eventQueueView.id] = eventQueueView;
                                });
                                bundle.clearedEventIds?.forEach((id) => {
                                    delete eventQueueViewsRef.current[id];
                                });
                            });
                            getListeners?.().forEach((listener) => listener.onBootstrap());
                        }
                    })
                    .catch(() => {
                        queryClient.invalidateQueries(['event-queue-views-bootstrap']);
                    });
            } else {
                return eventQueueViewsBootstrapQuery.refetch();
            }
        },
    });
}
